import { styled } from "@pigment-css/react";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import type { GlobalTokens } from "@acto/web-common/theme";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const meetingRowBase = (theme: GlobalTokens) => ({
  display: "flex",
  // Top-align the time box with the name so it sits flush with the first line when the name wraps.
  alignItems: "flex-start" as const,
  gap: theme.fieldSalesTwo.spacing[16],
  // padding grows the hover/press pill's hit area; the equal negative margin cancels it
  // in layout so rows still sit flush and the container gap is unaffected.
  padding: theme.fieldSalesTwo.spacing[8],
  margin: `-${theme.fieldSalesTwo.spacing[8]}`,
  border: "none",
  background: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[20],
  textAlign: "left" as const,
  font: "inherit",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: "background-color 120ms ease, transform 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
});

/** A plain button; wrap in a Radix `asChild` trigger for other roles (e.g. radio). */
export const MeetingRow = styled("button")(({ theme }) => ({
  ...meetingRowBase(theme),
  ...pressInteraction(theme),
}));

/**
 * {@link MeetingRow} without the shrink-on-press, for rows that stay on screen
 * after the tap (e.g. selection rows), where the release makes the row visibly
 * jump.
 */
export const NonScalingMeetingRow = styled("button")(({ theme }) => ({
  ...meetingRowBase(theme),
  ...pressInteraction(theme, { scale: 1 }),
}));

export const MeetingSectionLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

export const MeetingTimeBox = styled("span")(({ theme }) => ({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 56,
  height: 52,
  borderRadius: theme.fieldSalesTwo.radius[16],
  background: theme.fieldSalesTwo.colors.neutral[200],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

export const MeetingRowInfo = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  minWidth: 0,
}));

export const MeetingRowName = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  // Break long names and clamp to 2 lines so a dense list row stays compact.
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  overflowWrap: "break-word",
}));

export const MeetingRowAddress = styled("span")(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

export const formatMeetingRowAddress = (meeting: Meeting): string =>
  [meeting.customer.street, meeting.customer.city].filter(Boolean).join(" · ");
